import type { IndustrySetupInput } from "./types";

export type PersonalTrainerSetup = NonNullable<IndustrySetupInput["personalTrainerSetup"]>;

export function createPersonalTrainerInput(displayName: string, t: (key: string) => string): IndustrySetupInput {
  return {
    displayName,
    installOnly: false,
    selectedRecommendedAppCodes: null,
    personalTrainerSetup: {
      services: [{ key: "service-1", name: t("personalTrainer.defaultService"), durationMinutes: 60, price: null }],
    },
  };
}

export function validPersonalTrainerSetup(input: IndustrySetupInput): boolean {
  if (input.installOnly) return true;
  const services = input.personalTrainerSetup?.services;
  // Drafts created before the service editor keep their original training duration.
  if (!services) return Number.isInteger(input.defaultDurationMinutes)
    && (input.defaultDurationMinutes ?? 0) >= 5 && (input.defaultDurationMinutes ?? 0) <= 480;
  return services.length > 0 && services.length <= 50
    && new Set(services.map((service) => service.key)).size === services.length
    && new Set(services.map((service) => service.name.trim().toLowerCase())).size === services.length
    && services.every((service) => /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(service.key)
      && service.name.trim().length > 0 && service.name.trim().length <= 255
      && Number.isInteger(service.durationMinutes) && service.durationMinutes >= 5 && service.durationMinutes <= 480
      && (service.price == null || /^[0-9]{1,8}(?:\.[0-9]{1,2})?$/.test(service.price)));
}

export function parsePersonalTrainerSetupDraft(value: unknown): PersonalTrainerSetup | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const setup = value as Record<string, unknown>;
  if (!Array.isArray(setup.services) || setup.services.length > 50) return undefined;
  if (!setup.services.every((service: unknown) => {
    if (!service || typeof service !== "object" || Array.isArray(service)) return false;
    const entry = service as Record<string, unknown>;
    return typeof entry.key === "string" && typeof entry.name === "string"
      && typeof entry.durationMinutes === "number" && (entry.price == null || typeof entry.price === "string");
  })) return undefined;
  return value as PersonalTrainerSetup;
}
